import { useEffect, useState } from 'react';
import {
  Accordion,
  Button,
  Caption,
  IconButton,
  Section,
} from '@telegram-apps/telegram-ui';
import {
  BoxOwnerView,
  PrepareShareResponse,
  type BoxFormat,
} from '@veylme/shared';

import { apiFetch, emitEvent } from '../api/client.js';
import { ApiAuthError, ApiError, ApiRateLimitError } from '../api/errors.js';
import { CodeShareModal } from '../components/CodeShareModal.js';
import { SkeletonRows } from '../components/Skeleton.js';
import {
  isPremiumUser,
  shareMessageOrUnsupported,
  shareToStoryOrUnsupported,
} from '../lib/tg.js';
import { CheckIcon, CopyIcon, LinkIcon } from '../ui/icons.js';
import { useT } from '../hooks/useT.js';
import styles from './InvitePanel.module.css';

type InviteShareState =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'notice'; tone: 'info' | 'error'; text: string };

export interface InvitePanelProps {
  /**
   * Загруженный ящик автора (POST /boxes) — источник ссылки и карточки.
   * Ask-использование (InboxScreen): родитель грузит бокс заранее и передаёт
   * его сюда готовым, панель открыта по умолчанию.
   */
  box?: BoxOwnerView;
  /**
   * work-036 (Трек D): формат для ЛЕНИВОЙ загрузки, когда `box` не передан
   * (например message-ящик в Настройках). Панель НЕ грузит бокс на mount —
   * запрос `POST /boxes {format}` уходит только когда пользователь реально
   * раскрывает аккордеон (развилка Б: не плодить ящик у всех подряд).
   */
  format?: BoxFormat;
  /** Текст пояснения над полем ссылки — по умолчанию t.inbox_share_hint. */
  shareHint?: string;
}

/**
 * InvitePanel — «Ссылка для приглашений» наверху вкладки «Вопросы»: ссылка на
 * ящик + копирование + кнопки шеринга (Story/чат), всё в Accordion (открыт по
 * умолчанию для уже загруженного ask-бокса). Тумблер приёма вопросов («Твой
 * ящик») живёт отдельно в Настройках (BoxPanel).
 *
 * CANON G2/I4: shareStory / shareMessage вызываются ТОЛЬКО из onClick-хендлеров.
 */
export function InvitePanel({ box: boxProp, format, shareHint }: InvitePanelProps) {
  const t = useT();
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>(
    'idle',
  );
  const [inviteShare, setInviteShare] = useState<InviteShareState>({
    kind: 'idle',
  });
  // Ask-режим (box уже готов) — раскрыта по умолчанию, её видно сразу.
  // Lazy-режим (format, box не передан) — свёрнута: раскрытие аккордеона САМО
  // и есть «реальное открытие», которое триггерит ленивую загрузку ниже.
  const [linkOpen, setLinkOpen] = useState(boxProp !== undefined);
  // work-020: код-модалка для не-Premium (Premium-гейт в handleInviteStory).
  const [codeModalOpen, setCodeModalOpen] = useState(false);
  // Lazy-загрузка (work-036): бокс запрашивается только при linkOpen===true
  // и только один раз (lazyBox остаётся закэширован при повторном сворачивании).
  const [lazyBox, setLazyBox] = useState<BoxOwnerView | null>(null);
  const [lazyError, setLazyError] = useState(false);

  useEffect(() => {
    if (boxProp !== undefined || format === undefined) return;
    if (!linkOpen || lazyBox !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const loaded = await apiFetch('/boxes', {
          method: 'POST',
          body: { format },
          parse: BoxOwnerView,
        });
        if (!cancelled) setLazyBox(loaded);
      } catch {
        if (!cancelled) setLazyError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [boxProp, format, linkOpen, lazyBox]);

  const boxValue = boxProp ?? lazyBox;

  // Бокс ещё не готов (lazy-режим до раскрытия/до ответа сервера) — рисуем тот
  // же Accordion-каркас со skeleton/ошибкой внутри, кнопки шеринга не рендерим
  // (им нужен реальный box.inviteCardUrl/deepLink).
  if (boxValue === null) {
    return (
      <Section>
        <Accordion expanded={linkOpen} onChange={setLinkOpen}>
          <Accordion.Summary className={styles.accHeader}>
            {t.box_link_accordion}
          </Accordion.Summary>
          <Accordion.Content>
            <div className={styles.accordionInner}>
              {lazyError ? (
                <Caption Component="p" className={styles.noDeeplink}>
                  {t.inbox_no_deeplink}
                </Caption>
              ) : (
                <SkeletonRows count={2} withLead={false} />
              )}
            </div>
          </Accordion.Content>
        </Accordion>
      </Section>
    );
  }

  const box = boxValue;

  async function handleCopy(deepLink: string) {
    try {
      await navigator.clipboard.writeText(deepLink);
      setCopyStatus('copied');
      setTimeout(() => setCopyStatus('idle'), 1500);
    } catch {
      setCopyStatus('failed');
      setTimeout(() => setCopyStatus('idle'), 1500);
    }
  }

  function handleInviteStory() {
    if (box.inviteCardUrl === null) return;
    // work-020 (решение 2): кликабельную ссылку в сторис Telegram даёт ТОЛЬКО
    // Premium. Не-Premium → открываем код-модалку (её share-кнопка шлёт
    // widgetLinkUrl:'' → нативного Premium-апселла нет; чинит латентный баг W2).
    // emitEvent тут НЕ шлём — событие шлёт сама модалка на реальный 'shared'.
    if (!isPremiumUser()) {
      setCodeModalOpen(true);
      return;
    }
    setInviteShare({ kind: 'busy' });
    // work-035: widgetLink → story-tagged URL (`bs<code>`, атрибуция канала 'story').
    const result = shareToStoryOrUnsupported(box.inviteCardUrl, {
      text: t.tagline,
      widgetLinkUrl: box.storyShareUrl ?? '',
      widgetLinkName: t.share_ask_action,
    });
    if (result === 'shared') {
      setInviteShare({ kind: 'idle' });
      // Веха 8: авторское share-событие (CANON G2 — в onClick после результата).
      void emitEvent({ type: 'invite_shared_to_story', channel: 'story' });
    } else if (result === 'unsupported') {
      setInviteShare({ kind: 'notice', tone: 'info', text: t.inbox_story_unsupported });
    } else {
      setInviteShare({ kind: 'notice', tone: 'error', text: t.inbox_story_error });
    }
  }

  async function handleInviteChat() {
    if (box.inviteCardUrl === null) return;
    setInviteShare({ kind: 'busy' });
    try {
      const prepared = await apiFetch('/share/prepare', {
        method: 'POST',
        body: { kind: 'invite' },
        parse: PrepareShareResponse,
      });
      const result = await shareMessageOrUnsupported(prepared.preparedMessageId);
      if (result === 'shared') {
        setInviteShare({ kind: 'idle' });
        // Веха 8: авторское share-событие через чат (канал 'chat').
        void emitEvent({ type: 'chat_shared', channel: 'chat' });
      } else if (result === 'unsupported') {
        setInviteShare({ kind: 'notice', tone: 'info', text: t.inbox_chat_unsupported });
      } else {
        setInviteShare({ kind: 'notice', tone: 'error', text: t.inbox_chat_error });
      }
    } catch (err: unknown) {
      let text = t.inbox_chat_prepare_error;
      if (err instanceof ApiRateLimitError) {
        text = t.inbox_rate_limit;
      } else if (err instanceof ApiAuthError) {
        text = t.ask_error_restart;
      } else if (err instanceof ApiError) {
        text = err.message;
      }
      setInviteShare({ kind: 'notice', tone: 'error', text });
    }
  }

  return (
    <Section>
      <Accordion expanded={linkOpen} onChange={setLinkOpen}>
        <Accordion.Summary className={styles.accHeader}>
          {t.box_link_accordion}
        </Accordion.Summary>
        <Accordion.Content>
          <div className={styles.accordionInner}>
            {box.deepLink !== null ? (
              <div className={styles.sectionRow}>
                <Caption Component="p" className={styles.shareHint}>
                  {shareHint ?? t.inbox_share_hint}
                </Caption>
                <div className={styles.linkRow}>
                  <div className={styles.linkField}>
                    <LinkIcon className={styles.linkFieldIcon} />
                    <input
                      readOnly
                      value={box.deepLink}
                      onFocus={(e) => e.currentTarget.select()}
                      className={styles.linkInput}
                    />
                  </div>
                  <div className={styles.copyWrap}>
                    <IconButton
                      mode="plain"
                      size="m"
                      aria-label={
                        copyStatus === 'copied'
                          ? t.inbox_copied
                          : copyStatus === 'failed'
                            ? t.inbox_copy_error
                            : t.inbox_copy
                      }
                      title={
                        copyStatus === 'copied'
                          ? t.inbox_copied
                          : copyStatus === 'failed'
                            ? t.inbox_copy_error
                            : t.inbox_copy
                      }
                      onClick={() => handleCopy(box.deepLink as string)}
                    >
                      {copyStatus === 'copied' ? <CheckIcon /> : <CopyIcon />}
                    </IconButton>
                  </div>
                </div>
              </div>
            ) : (
              <div className={styles.sectionRow}>
                <Caption Component="p" className={styles.noDeeplink}>
                  {t.inbox_no_deeplink}
                </Caption>
              </div>
            )}

            <div className={styles.inviteBtns}>
              {/* Главная CTA: «поделиться в чат» работает у ВСЕХ пользователей
                  (без Premium-гейта) и шлёт кликабельную карточку-приглашение
                  через shareMessage. «В историю» — второстепенная: для
                  не-Premium ведёт в тупиковый код-модал (см. handleInviteStory). */}
              <Button
                mode="filled"
                size="m"
                stretched
                onClick={handleInviteChat}
                disabled={box.inviteCardUrl === null || inviteShare.kind === 'busy'}
              >
                {t.inbox_invite_chat}
              </Button>
              <Button
                mode="bezeled"
                size="m"
                stretched
                onClick={handleInviteStory}
                disabled={box.inviteCardUrl === null || inviteShare.kind === 'busy'}
              >
                {t.inbox_invite_story}
              </Button>
              {/* Лёгкий nudge для не-Premium: ссылка в Story без Premium не
                  кликабельна (показываем код вместо неё) — чат надёжнее. */}
              {!isPremiumUser() && (
                <Caption Component="p" className={styles.storyNonPremiumHint}>
                  {t.inbox_story_nonpremium_hint}
                </Caption>
              )}
              {box.inviteCardUrl === null && (
                <Caption Component="p" className={styles.cardRenderingHint}>
                  {t.inbox_card_rendering}
                </Caption>
              )}
              {inviteShare.kind === 'notice' && (
                <Caption
                  Component="p"
                  role={inviteShare.tone === 'error' ? 'alert' : undefined}
                  className={`${styles.shareNotice} ${inviteShare.tone === 'error' ? styles.shareNoticeError : styles.shareNoticeInfo}`}
                >
                  {inviteShare.text}
                </Caption>
              )}
            </div>
          </div>
        </Accordion.Content>
      </Accordion>

      {/* work-020: код-модалка не-Premium. Рендерим только при готовой карточке
          (inviteCardUrl) — share-кнопка модалки требует media-URL. */}
      {box.inviteCardUrl !== null && (
        <CodeShareModal
          open={codeModalOpen}
          onClose={() => setCodeModalOpen(false)}
          inviteCardUrl={box.inviteCardUrl}
        />
      )}
    </Section>
  );
}
